import "./carousel.scss";

import React, { useCallback, useContext, useEffect, useRef, useState } from "react";

import { isTouchCapable, LogVerbose } from "../../../services/utilities";
import { CaretDisplayOption } from "../../../types/types";
import { Swipeable } from "../Swipeable/Swipeable";
import { CarouselCarets } from "./CarouselCarets";
import { carouselContext, CarouselContext } from "./carouselContext";
import { CarouselDotGroup } from "./CarouselDotGroup";

export const CarouselScroll = ({ tag, children, withCaret, withDotGroup, transitionDurationInSeconds }:
    {
        tag?: string; withDotGroup?: boolean; withCaret?: CaretDisplayOption; transitionDurationInSeconds?: number;
        children: React.ReactNode[]
    }) => {

    const nChildren = children.length;
    const { centerIndex, nSlides, setNSlides, name, activeIndex, gotoNextSlide, gotoPreviousSlide, refSlideIndex } = useContext<CarouselContext>(carouselContext);
    const [firstRender, setFirstRender] = useState(true);
    // const [heightCalculated, setHeightCalculated] = useState(false);
    // const [visible, setVisible] = useState(false);

    const carouselRef = useRef<HTMLDivElement>(null);
    const stageRef = useRef<HTMLDivElement>(null);
    const observerRef = useRef<ResizeObserver | null>(null);

    // Sync nSlides with nChildren
    useEffect(() => {
        if (nChildren !== nSlides) {
            console.warn('CarouselScroll: nChildren != nSlides', nChildren, nSlides);
            setNSlides(nChildren);
        }
    }, []);

    // Function to apply height to other slides
    const applyHeightToOtherSlides = useCallback((refSlideHeight: number) => {
        if (!carouselRef.current)
            return;

        LogVerbose(`CarouselScroll: updating info slide height according to # ${refSlideIndex} slide. Target height: ${refSlideHeight}px`);

        const allSlides = Array.from(carouselRef.current.children) as HTMLElement[];
        allSlides.forEach((slide, index) => {
            if (index !== refSlideIndex) {
                slide.style.height = `${refSlideHeight}px`;
                slide.style.visibility = 'visible';
            }
        });

        carouselRef.current.style.height = `${refSlideHeight}px`;
        if (stageRef.current) {
            stageRef.current.style.height = `${refSlideHeight}px`;
        }
        // setHeightCalculated(true);
    }, [refSlideIndex]);

    // Set up ResizeObserver for the reference slide
    useEffect(() => {
        // Only set up observer if we have a valid refSlideIndex
        if (nSlides < 2 || !carouselRef.current || refSlideIndex === undefined || refSlideIndex < 0 || refSlideIndex >= nSlides) {
            return;
        }

        // Clean up existing observer
        if (observerRef.current) {
            observerRef.current.disconnect();
        }

        // eslint-disable-next-line security/detect-object-injection
        const refSlide = carouselRef.current.children[refSlideIndex] as HTMLElement;
        if (!refSlide) return;

        observerRef.current = new ResizeObserver((entries) => {
            for (const entry of entries) {
                const refSlideHeight = entry.contentRect.height;
                if (refSlideHeight > 0) {
                    applyHeightToOtherSlides(refSlideHeight);
                }
            }
        });

        observerRef.current.observe(refSlide);

        // Reset height calculation state when refSlideIndex changes
        // setHeightCalculated(false);

        // Cleanup function
        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect();
                observerRef.current = null;
            }
        };
    }, [refSlideIndex, nSlides, applyHeightToOtherSlides]);

    useEffect(() => {
        LogVerbose(`CarouselScroll: activeIndex changed: ${activeIndex}`);
        if (!carouselRef.current) return;

        if (!centerIndex || nSlides < 1) {
            LogVerbose(`translateX(-${activeIndex * 100}%)`);
            carouselRef.current.style.transitionDuration = transitionDurationInSeconds ? `${transitionDurationInSeconds}s` : '1s';
            carouselRef.current.style.transform = `translateX(-${activeIndex * 100}%)`;
        } else {
            const baseTranslateX = -100 * (centerIndex + 0.5);
            const extraDrift = (centerIndex - activeIndex) * 100;
            LogVerbose(`translateX(${baseTranslateX + extraDrift}%)`);

            if (firstRender) {
                carouselRef.current.style.transitionDuration = '0s';
                setFirstRender(false);
            } else {
                carouselRef.current.style.transitionDuration = transitionDurationInSeconds ? `${transitionDurationInSeconds}s` : '1s';
            }
            carouselRef.current.style.transform = `translateX(${baseTranslateX + extraDrift}%)`;
        }
    }, [activeIndex, centerIndex, nSlides, firstRender]);

    const getId = useCallback((index: number) => {
        let nameTag = name || 'carousel';
        if (tag) {
            nameTag = `${nameTag}_${tag}`;
        }
        return `${nameTag}_${index}`;
    }, [name, tag]);

    // Early return for empty carousel
    if (nChildren === 0) return null;

    const showCaret = withCaret === 'Always' || (withCaret === 'NonTouch' && !isTouchCapable());
    const carouselInnerClass = centerIndex ? 'carousel-scroll-inner centerize' : 'carousel-scroll-inner';

    return (
        <>
            <div data-testid='carousel-stage' className='scroll-stage' ref={stageRef} >
                <Swipeable onSwipedLeft={gotoNextSlide} onSwipedRight={gotoPreviousSlide} swipeableClass='carousel-scroll'>
                    <div className={carouselInnerClass} ref={carouselRef}>
                        {children.map((slide, index) => {

                            return (
                                <div
                                    data-testid='carousel-slide'
                                    className={`carousel-scroll-item ${index === activeIndex ? 'active' : 'non-active'}`}
                                    key={index}
                                    id={getId(index)}
                                // style={{ visibility: shouldHide ? 'hidden' : 'visible' }}

                                >
                                    {slide}
                                </div>
                            );
                        })}
                    </div>
                </Swipeable>
                {showCaret && <CarouselCarets />}
            </div>
            {withDotGroup && <CarouselDotGroup />}
        </>
    );
};

